import { useEffect, useState } from "react";
import { API_BASE, getAuthHeaders } from "../../api/client";
import { locationScopeQuery } from "../../lib/storeScope";
import { ladderRejectNotice } from "../../lib/scheduleLadder";

/** Preference summary + active reject reason. Week status pill is in the builder top bar. */
export function ScheduleLadderBar({
  lang,
  store,
  unifiedLocationId,
  weekStartISO,
  onWeekChange,
  refreshToken,
}) {
  const [week, setWeek] = useState(null);
  const [prefs, setPrefs] = useState([]);

  useEffect(() => {
    if (!store || !weekStartISO) return undefined;
    const controller = new AbortController();
    const scope = locationScopeQuery(unifiedLocationId);
    const qs = [
      scope,
      `week_start=${encodeURIComponent(weekStartISO)}`,
      `store_id=${encodeURIComponent(store)}`,
    ]
      .filter(Boolean)
      .join("&");

    fetch(`${API_BASE}/schedule/weeks/approval?${qs}`, {
      headers: getAuthHeaders(),
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (controller.signal.aborted || !body) return;
        setWeek(body);
        onWeekChange?.(body);
      })
      .catch(() => {});

    fetch(`${API_BASE}/schedule/preferences?${qs}&status=open`, {
      headers: getAuthHeaders(),
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : { preferences: [] }))
      .then((body) => {
        if (controller.signal.aborted) return;
        setPrefs(Array.isArray(body?.preferences) ? body.preferences : []);
      })
      .catch(() => {
        if (!controller.signal.aborted) setPrefs([]);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, weekStartISO, unifiedLocationId, refreshToken]);

  const cannotCount = prefs.filter((p) => p.kind === "cannot").length;
  const preferCount = prefs.filter((p) => p.kind === "prefer_not").length;
  const changeCount = prefs.filter((p) => p.kind === "change_request").length;
  const rejectNotice = ladderRejectNotice(week, lang);
  const showBar = prefs.length > 0 || Boolean(rejectNotice);
  if (!showBar) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "10px 12px",
        marginBottom: 10,
        border: "1px solid var(--border)",
        borderRadius: 12,
        background: "var(--surface2)",
      }}
    >
      {rejectNotice ? (
        <div
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            background: "#FEF2F2",
            border: "1px solid #FECACA",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, color: "#991B1B", marginBottom: 4 }}>
            {rejectNotice.title}
            {rejectNotice.meta ? (
              <span style={{ fontWeight: 600, color: "#B91C1C" }}> · {rejectNotice.meta}</span>
            ) : null}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#7F1D1D", lineHeight: 1.45 }}>
            {rejectNotice.comment}
          </div>
        </div>
      ) : null}
      {prefs.length > 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 600 }}>
          {lang === "gr" ? "Προτιμήσεις εβδομάδας" : "Week preferences"}
          {": "}
          {[
            cannotCount
              ? lang === "gr"
                ? `${cannotCount} δεν μπορεί`
                : `${cannotCount} cannot`
              : null,
            preferCount
              ? lang === "gr"
                ? `${preferCount} προτίμηση όχι`
                : `${preferCount} prefer not`
              : null,
            changeCount
              ? lang === "gr"
                ? `${changeCount} αίτημα αλλαγής`
                : `${changeCount} change request`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      ) : null}
    </div>
  );
}
